import { AppSettings } from "@/AppSettings";
import { ConfigurationStatus, CoordinatingGroup, MasterGroup, Trial } from "@/api/dtos";
import Multiselect, { IMultiselectProps } from "@/components/forms/inputs/Multiselect";
import Text, { ITextProps } from "@/components/forms/inputs/Text";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import TrialContext from "@/contexts/data/TrialContext";
import { faExclamationCircle } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, ListItemIcon, ListItemText, darken, lighten, styled } from "@mui/material";
import { FC, useCallback, useContext, useMemo } from "react";
import StatusList, { IStatusListCreateAdditional, IStatusListCreateConfigureLink, IStatusListCreateLabel, IStatusListCreateLink, IStatusListIsDisabled, IStatusListSearch, IStatusListStatsComponentProps, StatusListItemIcon, StatusListItemText, StatusListProps } from "./StatusList";
import { DefaultSortableFields, IStatusListSortable } from "./StatusListSort";
import DefaultStatComponents, { StatusListItemTooltip, StatusListStatsBox } from './StatusListStats';
import ChosenThemeSizeContext from "@/contexts/styles/ChosenThemeSizeContext";


export interface ICoordinatingGroupConfigurationListAdditional {
    trial?: Trial;
    masterGroup?: MasterGroup;
}

export interface ICoordinatingGroupStatusListProps extends StatusListProps<CoordinatingGroup, ICoordinatingGroupConfigurationListAdditional> {

}


const MasterGroupBox = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    background: theme.palette.mode === 'dark' ? darken(theme.palette.primary.main,0.5) : lighten(theme.palette.primary.main, 0.5)
}));

const createCoordinatingGroupStatusLabel: IStatusListCreateLabel<CoordinatingGroup, ICoordinatingGroupConfigurationListAdditional> = (itemData) => {
    return (
        <>
            <StatusListItemIcon>
                <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.coordinatingGroup} />
            </StatusListItemIcon>
            <StatusListItemText 
                primary={itemData.data.name} 
            />
        </>
    )
};

const createCoordinatingGroupLink: IStatusListCreateLink<CoordinatingGroup, ICoordinatingGroupConfigurationListAdditional> = (itemData) => {
    if (itemData.additional.trial) {
        return `/${itemData.additional.trial.oid}/configuration/coordinating-groups/${itemData.data.code}`
    }

    return `/configuration/coordinating-groups/${itemData.data.code}`;
};

const createCoordinatingGroupConfigurationLink: IStatusListCreateConfigureLink<CoordinatingGroup, ICoordinatingGroupConfigurationListAdditional> = (itemData) => {
    return undefined;
};

const isCoordinatingGroupDisabled: IStatusListIsDisabled<CoordinatingGroup, ICoordinatingGroupConfigurationListAdditional> = (itemData) => {
    return !itemData.data.status || itemData.data.status === ConfigurationStatus.Disabled;
};

const isCoordinatingGroupListItemDisabled: IStatusListIsDisabled<CoordinatingGroup, ICoordinatingGroupConfigurationListAdditional> = (itemData) => {
    return false;
};

const CoordinatingGroupStatsComponent = ({
    itemData, 
    theme
}: IStatusListStatsComponentProps<CoordinatingGroup, ICoordinatingGroupConfigurationListAdditional>) => {
    return (
        
        <StatusListStatsBox>
            <DefaultStatComponents.DisabledIndicatorStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.ReadOnlyIndicatorStats itemData={itemData} theme={theme} />
            {
                itemData.additional.masterGroup?.name && (
                    <StatusListItemTooltip
                        placement="left"
                        title={
                            <>
                                <strong>Master Group</strong>
                                <div>
                                    {itemData.additional.masterGroup.name}
                                </div>
                            </>
                        }
                    >
                        <MasterGroupBox>
                            {itemData.additional.masterGroup.name}
                        </MasterGroupBox>
                    </StatusListItemTooltip>
                )
            }
        </StatusListStatsBox>
    )
};


const SortableFields: IStatusListSortable<CoordinatingGroup, ICoordinatingGroupConfigurationListAdditional>[] = [
    {
        name: 'Name',
        sort: (a, b) => a.data.name! > b.data.name! ? 1 : -1
    },
    {
        name: 'Master Group',
        sort: (a, b) => (a.additional.masterGroup?.name ? a.additional.masterGroup.name : '') > (b.additional.masterGroup?.name ? b.additional.masterGroup.name : '') ? 1 : -1
    },
    DefaultSortableFields.status
]

const DefaultSearchData = { statuses: [ConfigurationStatus.Enabled, ConfigurationStatus.ReadOnly] };

const CoordinatingGroupConfigurationList: FC<ICoordinatingGroupStatusListProps> = ({
    ...statusListProps
}) => {
    const { data: trial } = useContext(TrialContext);
    const { data: masterGroups, idMap: masterGroupMapping } = useContext(MasterGroupsContext);

    const createCoordinatingGroupAdditional: IStatusListCreateAdditional<CoordinatingGroup, ICoordinatingGroupConfigurationListAdditional> = useCallback((itemData) => {
        return {
            trial: trial,
            masterGroup: masterGroupMapping && itemData.data.masterGroupId ? masterGroupMapping[itemData.data.masterGroupId] : undefined,
        }
    }, [trial, masterGroups, masterGroupMapping]);

    

    const searchableFields: IStatusListSearch<CoordinatingGroup, ICoordinatingGroupConfigurationListAdditional>[] = useMemo(() => {
        const output: IStatusListSearch<CoordinatingGroup, ICoordinatingGroupConfigurationListAdditional, any, any>[] = [
            {
                key: 'name',
                component: Text,
                componentProps: {
                    margin: 'normal'
                } as ITextProps,
                label: 'Name',
                searchFn: (item, value) => value ? item.data.name?.toLowerCase()?.includes(value?.toLowerCase()) ?? false : false
            } as IStatusListSearch<CoordinatingGroup, ICoordinatingGroupConfigurationListAdditional, string>
        ]

        if (masterGroups && masterGroups.length > 0) {
            output.push({
                key: 'masterGroups',
                component: Multiselect,
                componentProps: {
                    options: masterGroups?.map(x => ({
                        id: x.id,
                        name: x.name
                    })) ?? [],
                    FormControlProps: {
                        margin: 'normal'
                    }
                } as IMultiselectProps,
                label: 'Master Groups',
                searchFn: (item, value) => value && value.length > 0 ?
                    item.data.masterGroupId ?
                        value.includes(item.data.masterGroupId) ?? false : 
                        false :
                    true
            } as IStatusListSearch<CoordinatingGroup, ICoordinatingGroupConfigurationListAdditional, number[]>);
        }

        output.push({
            key: 'statuses',
            component: Multiselect,
            componentProps: {
                options: [
                    {
                        id: ConfigurationStatus.Disabled,
                        name: 'Disabled',
                        description: `The coordinating group is disabled in the Source Data Verification system.`
                    },
                    {
                        id: ConfigurationStatus.ReadOnly,
                        name: 'Read Only',
                        description: `The coordinating group is read only in the Source Data Verification system.`
                    },
                    {
                        id: ConfigurationStatus.Enabled,
                        name: 'Enabled',
                        description: `The coordinating group is enabled in the Source Data Verification system.`
                    }
                ],
                FormControlProps: {
                    margin: 'normal'
                }
                
            } as IMultiselectProps,
            label: 'Statuses',
            searchFn: (item, value) => value && value.length > 0 ?
                item.data.status ?
                    value.includes(item.data.status) ?? false : 
                    false :
                true
        } as IStatusListSearch<CoordinatingGroup, ICoordinatingGroupConfigurationListAdditional, ConfigurationStatus[]>)

        return output;
    }, []);
    

    const themeSize = useContext(ChosenThemeSizeContext);

    const height = useMemo(() => {
        switch (themeSize) {
            case 'extra-small':
                return 25;
            case 'small':
                return 37;
            case 'medium':
                return 49;
            case 'large':
                return 61;
            case 'extra-large':
                return 73;
        }
    }, [themeSize])
    
    return (
        <StatusList 
            {...statusListProps}
            createLabel={createCoordinatingGroupStatusLabel}
            createLink={createCoordinatingGroupLink}
            createConfigureLink={createCoordinatingGroupConfigurationLink}
            isDisabled={isCoordinatingGroupDisabled}
            isListItemDisabled={isCoordinatingGroupListItemDisabled}
            createAdditional={createCoordinatingGroupAdditional}
            StatsComponent={CoordinatingGroupStatsComponent}
            sortKey="configuration-coordinating-groups-sort"
            sortableFields={SortableFields}
            noRecords={
                <Alert
                    severity="warning"
                >
                    <AlertTitle>No Coordinating Groups</AlertTitle>
                    No coordinating groups were found on the {trial?.name} trial.
                </Alert>
            }
            searchFields={searchableFields}
            searchKey={`configuration-coordinating-groups-${trial?.oid}`}
            fixedItemHeight={height}
            defaultSearchData={DefaultSearchData}
        />
    )
};

export default CoordinatingGroupConfigurationList;